/**
 * Normalisation de la réponse de `page/optimisation` pour les Impacts globaux.
 *
 * L'API renvoie des champs à plat, un par couple (horizon, scénario) :
 *   R2020_2degre … R2050_2degre, RMoy_2degre
 *   R2020_4degre … R2050_4degre, RMoy_4degre
 *   R2020_hybride … R2050_hybride, RMoy_hybride
 *   Rreference_tracc, R2_tracc, R2.7_tracc, RMoy_tracc
 *
 * Les reprendre tels quels imposerait un état par champ (28 au total, contre 16
 * dans l'ancienne page qui n'en gérait que deux scénarios). On les range donc
 * une fois pour toutes dans une structure indexée :
 *
 *   risques[scenario][horizon] -> tableau des niveaux de risque
 *   moyennes[scenario]         -> tableau indexé par position d'horizon
 *
 * Conventions de lecture (inchangées par rapport à l'ancienne page) :
 *  - un tableau R contient 5 valeurs, dont seules les 4 premières sont
 *    affichées : Très courant (3 ans), Courant (7 ans), Exceptionnel (20 ans),
 *    Catastrophique (50 ans) ;
 *  - RMoy est indexé par horizon, pas par niveau de risque.
 */

// libelleHorizon/horizonsSelectionnables vivent maintenant dans
// shared/utils/horizon.js : c'est une règle commune à TOUTE page ayant un
// couple sidebar "Année" + "Scénarios climatiques" (Classement, Substitution,
// etc.), pas seulement à Impacts globaux. Réexportées ici pour ne rien casser
// des imports existants qui pointent vers ce fichier.
export { libelleHorizon, horizonsSelectionnables } from './horizon';

/** Scénarios exposés par la sidebar, dans l'ordre d'affichage. */
export const SCENARIOS = ['2degre', '4degre', 'hybride', 'tracc'];

/** Les scénarios climatiques classiques raisonnent en années. */
export const ANNEES = [2020, 2025, 2030, 2035, 2040, 2045, 2050];

/**
 * TRACC raisonne en niveau de réchauffement et non en année : la référence
 * remplace 2020, +2 °C correspond à l'horizon 2035 et +2.7 °C à 2050.
 */
export const HORIZONS_TRACC = ['reference', '2', '2.7'];

/**
 * Hybride n'est renvoyé par l'API que pour les 3 horizons sélectionnables
 * (2020 = référence, 2035, 2050) : RMoy_hybride est donc un tableau compact
 * de 3 valeurs, PAS un tableau de 7 valeurs indexé comme RMoy_2degre/4degre
 * (une par année de 2020 à 2050). Réutiliser ANNEES.indexOf(...) dessus
 * renvoyait un index (3 pour 2035, 6 pour 2050) hors de ce tableau à 3
 * éléments -> `undefined`, d'où le risque moyen manquant en 2035/2050 dès
 * qu'on cochait Hybride.
 */
const HORIZONS_HYBRIDE = [2020, 2035, 2050];

/**
 * Les trois horizons tracés par le graphique du risque moyen : la référence
 * puis les deux échéances.
 */
export function horizonsGraphique(scenario) {
  return scenario === 'tracc'
    ? ['reference', '2', '2.7']
    : [2020, 2035, 2050];
}

/** Horizon de comparaison (la ligne « par rapport à ») selon le scénario. */
export function horizonReference(scenario) {
  return scenario === 'tracc' ? 'reference' : 2020;
}

/** Nom du champ renvoyé par l'API pour un couple (scénario, horizon). */
function champ(scenario, horizon) {
  if (scenario !== 'tracc') return `R${horizon}_${scenario}`;
  // Le point de "R2.7_tracc" est volontaire : en TRACC l'horizon est un niveau
  // de réchauffement (+2.7 °C), pas une année.
  return horizon === 'reference' ? 'Rreference_tracc' : `R${horizon}_tracc`;
}

/**
 * Position d'un horizon dans le tableau RMoy.
 * Classique (2degre/4degre) : 7 valeurs, une par année de 2020 à 2050.
 * Hybride : 3 valeurs (2020/2035/2050 uniquement).
 * TRACC : 3 valeurs, dans l'ordre référence / +2 / +2.7.
 */
function positionMoyenne(scenario, horizon) {
  if (scenario === 'tracc') return HORIZONS_TRACC.indexOf(horizon);
  if (scenario === 'hybride') return HORIZONS_HYBRIDE.indexOf(Number(horizon));
  return ANNEES.indexOf(Number(horizon));
}

/**
 * Transforme la réponse brute en { risques, moyennes }.
 * Un scénario absent de la réponse est simplement omis, ce qui permet de
 * brancher hybride et TRACC au fur et à mesure qu'ils arrivent côté API.
 */
export function normaliserImpacts(reponse) {
  if (!reponse) return { risques: {}, moyennes: {} };

  const risques = {};
  const moyennes = {};

  SCENARIOS.forEach((scenario) => {
    const horizons = scenario === 'tracc' ? HORIZONS_TRACC : ANNEES;
    const parHorizon = {};
    let auMoinsUn = false;

    horizons.forEach((horizon) => {
      const valeurs = reponse[champ(scenario, horizon)];
      if (Array.isArray(valeurs)) {
        parHorizon[horizon] = valeurs;
        auMoinsUn = true;
      }
    });

    if (auMoinsUn) {
      risques[scenario] = parHorizon;
      moyennes[scenario] = reponse[`RMoy_${scenario}`] ?? [];
    }
  });

  return { risques, moyennes };
}

/** Risque moyen d'un horizon donné, ou undefined si indisponible. */
export function moyennePourHorizon(moyennes, scenario, horizon) {
  const position = positionMoyenne(scenario, horizon);
  return position >= 0 ? moyennes[scenario]?.[position] : undefined;
}
